import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../../../store/user/user.selector';
import { setSystemNotification } from '../../../../store/system/system.action';
import { api } from '../../../../api/rtk';
import { listenToDiscordCards, removeCardFromDiscord, createDiscordLot, ungroupDiscordLot } from '../../../../utils/firebase/discordQueue';
import { promoteEbayItemsToSales } from '../../../../utils/firebase/ebayToSales';
import { reassignStagedItems } from '../../../../utils/firebase/stagedQueue';
import { isLotItem, isSingleItem, buildTitle } from '../../../../utils/whatnot/whatnotListingText';
import { itemMatchesPlayerSearch } from '../../../../utils/ebay/ebayQueueSearch';
import { sumStoredListPrices } from '../../../../utils/ebay/ebayLotPrice';
import CardAttribute from '../../../../components/card-attribute/CardAttribute';
import InputBar from '../../../../components/input-bar/InputBar';
import {
    EmptyState,
    LoadingState,
    QueueHeader,
    QueueHeaderActions,
    CombineLotBtn,
    ShowSelectDropdown,
    WhatnotPageContainer,
} from '../../../whatnot/whatnot-backend/WhatnotPage.styles';

export default function DiscordQueue({ playerSearch = '', onSelectionChange, onListTotalChange }) {
    const user = useSelector(selectUser);
    const dispatch = useDispatch();
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [listedPending, setListedPending] = useState(false);
    const [removePending, setRemovePending] = useState(false);
    const [lotTitle, setLotTitle] = useState('');
    const [combineOpen, setCombineOpen] = useState(false);

    useEffect(() => {
        if (!user?.uid) { setIsLoading(false); return undefined; }
        setIsLoading(true);
        return listenToDiscordCards(user.uid, (cards) => {
            setItems(cards);
            setIsLoading(false);
        });
    }, [user?.uid]);

    useEffect(() => { setListedPending(false); setRemovePending(false); }, [selectedIds]);

    const visibleItems = useMemo(
        () => items.filter((item) => itemMatchesPlayerSearch(item, playerSearch)),
        [items, playerSearch]
    );
    const listTotal = useMemo(() => sumStoredListPrices(items), [items]);
    const allVisibleSelected = visibleItems.length > 0 && visibleItems.every((item) => selectedIds.has(item.id));
    const selectedSingles = useMemo(
        () => items.filter((item) => selectedIds.has(item.id) && isSingleItem(item)),
        [items, selectedIds]
    );

    const handleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            const visibleIds = visibleItems.map((item) => item.id);
            const allChecked = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
            if (allChecked) {
                const next = new Set(prev);
                visibleIds.forEach((id) => next.delete(id));
                return next;
            }
            const next = new Set(prev);
            visibleIds.forEach((id) => next.add(id));
            return next;
        });
    }, [visibleItems]);

    useEffect(() => {
        if (!onSelectionChange) return;
        onSelectionChange({
            allSelected: allVisibleSelected,
            disabled: isLoading || visibleItems.length === 0,
            toggle: handleSelectAll,
        });
    }, [onSelectionChange, allVisibleSelected, isLoading, visibleItems.length, handleSelectAll]);

    useEffect(() => {
        onListTotalChange?.(listTotal);
    }, [onListTotalChange, listTotal]);

    const toggle = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const handleMarkListed = useCallback(async () => {
        if (!user?.uid || selectedIds.size === 0) return;
        if (!listedPending) { setListedPending(true); return; }
        setListedPending(false);
        try {
            const selected = items.filter((item) => selectedIds.has(item.id));
            const { saleIds, cardCount } = await promoteEbayItemsToSales(user.uid, selected);
            dispatch(api.util.invalidateTags(['Sale']));
            setSelectedIds(new Set());
            dispatch(setSystemNotification(
                `${saleIds.length} listing${saleIds.length === 1 ? '' : 's'} (${cardCount} card${cardCount === 1 ? '' : 's'}) marked as listed`
            ));
        } catch (error) {
            dispatch(setSystemNotification('Failed to mark as listed'));
        }
    }, [user?.uid, selectedIds, listedPending, items, dispatch]);

    const handleMove = useCallback(async (platform) => {
        if (!user?.uid) return;
        const selected = items.filter((item) => selectedIds.has(item.id));
        await reassignStagedItems(user.uid, selected, platform);
        setSelectedIds(new Set());
    }, [user?.uid, items, selectedIds]);

    const handleRemove = useCallback(async () => {
        if (!user?.uid || selectedIds.size === 0) return;
        if (!removePending) { setRemovePending(true); return; }
        setRemovePending(false);
        await Promise.all([...selectedIds].map((id) => removeCardFromDiscord(user.uid, id)));
        setSelectedIds(new Set());
    }, [user?.uid, selectedIds, removePending]);

    const handleCreateLot = useCallback(async () => {
        if (!user?.uid || selectedSingles.length < 2 || !lotTitle.trim()) return;
        await createDiscordLot(
            user.uid,
            lotTitle,
            selectedSingles.map((item) => item.id),
            selectedSingles
        );
        setCombineOpen(false);
        setLotTitle('');
        setSelectedIds(new Set());
    }, [user?.uid, selectedSingles, lotTitle]);

    if (isLoading) {
        return <WhatnotPageContainer><LoadingState>Loading...</LoadingState></WhatnotPageContainer>;
    }

    return (
        <WhatnotPageContainer>
            {items.length === 0 ? (
                <EmptyState>
                    No Discord listings staged yet
                    <p>Add cards from the checklist.</p>
                </EmptyState>
            ) : (
                <>
                    <QueueHeader>
                        <QueueHeaderActions>
                            {selectedIds.size > 0 && (
                                <>
                                    <CombineLotBtn type="button" onClick={handleMarkListed}>
                                        {listedPending ? 'Confirm Listed?' : 'Listed'}
                                    </CombineLotBtn>
                                    <ShowSelectDropdown
                                        value=""
                                        aria-label="Move selected to another platform"
                                        onChange={(e) => { if (e.target.value) handleMove(e.target.value); }}
                                    >
                                        <option value="">Move to…</option>
                                        <option value="eBay">eBay</option>
                                        <option value="Whatnot">Whatnot</option>
                                    </ShowSelectDropdown>
                                    <CombineLotBtn
                                        type="button"
                                        disabled={selectedSingles.length < 2}
                                        onClick={() => setCombineOpen(true)}
                                    >
                                        Create Lot
                                    </CombineLotBtn>
                                    <CombineLotBtn type="button" onClick={handleRemove}>
                                        {removePending ? 'Confirm removal?' : 'Remove'}
                                    </CombineLotBtn>
                                </>
                            )}
                        </QueueHeaderActions>
                    </QueueHeader>

                    {visibleItems.length === 0 ? (
                        <EmptyState>
                            No listings match "{playerSearch.trim()}"
                        </EmptyState>
                    ) : visibleItems.map((item) => {
                        const label = isLotItem(item)
                            ? (item.title || 'Untitled Lot')
                            : (buildTitle(item) || 'Untitled');
                        return (
                            <div
                                key={item.id}
                                onClick={() => toggle(item.id)}
                                style={{
                                    display: 'flex',
                                    gap: 12,
                                    alignItems: 'center',
                                    padding: '10px 4px',
                                    cursor: 'pointer',
                                }}
                            >
                                <input type="checkbox" readOnly checked={selectedIds.has(item.id)} />
                                <div style={{ flex: 1 }}>
                                    <div>{label}</div>
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                        {item.series && <CardAttribute text={item.series} size="S" />}
                                        {item.cardNumber && <CardAttribute text={`#${item.cardNumber}`} size="S" />}
                                        {isLotItem(item) && <CardAttribute text={`${item.cardCount ?? item.cards?.length ?? 0} cards`} size="S" />}
                                    </div>
                                </div>
                                {isLotItem(item) && (
                                    <CardAttribute
                                        text="UNGROUP"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            ungroupDiscordLot(user.uid, item);
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}

                    {combineOpen && (
                        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <InputBar
                                    id="discord-lot-title"
                                    type="text"
                                    label="Lot title"
                                    value={lotTitle}
                                    updateValue={setLotTitle}
                                />
                            </div>
                            <CombineLotBtn type="button" onClick={handleCreateLot} disabled={!lotTitle.trim()}>
                                Create
                            </CombineLotBtn>
                            <CombineLotBtn type="button" onClick={() => setCombineOpen(false)}>Cancel</CombineLotBtn>
                        </div>
                    )}
                </>
            )}
        </WhatnotPageContainer>
    );
}
